import { Form } from 'antd';
import {
	Batch,
	BatchFormType,
} from '../../../../shared/VolunteerApplicationServiceTypes';
import { useCallback, useEffect, useState } from 'react';
import { useConfirmPopup } from '../../../../hooks/useConfirmPopup';
import { NavigateOptions, useNavigate } from 'react-router-dom';
import { useBatchId } from '../../../../hooks/useBatchId';
import { BATCH_HOME } from '../../../../shared/Routes';
import {
	createBatch,
	getBatch,
	publishBatch,
	updateBatch,
} from '../../../../service/VolunteerApplicationService';
import { useCallApi } from '../../../../hooks/useCallApi';
import { useTranslation } from 'react-i18next';
import { useInfoPopup } from '../../../../hooks/useInfoPopUp';
import ConfirmTick from '../../../../assets/images/confirm-tick.svg';
import { useToastMessage } from '../../../../hooks/useToastMessage';
import dayjs from 'dayjs';
import { Moment } from 'moment';
import { useAuth } from '../../../../hooks/useAuth';
import { formatDateToISOString } from '../../../../shared/Utils';
import useUserSession from '../../../../hooks/useUserSession';

let globalIsEditFormStatus = false;
const listeners: (() => void)[] = [];

export const useCreateOrViewOrEditBatch = () => {
	const [submitButtonDisabled, setSubmitButtonDisabled] =
		useState<boolean>(false);
	const [batch, setBatch] = useState<Batch | undefined>();
	const { getRouteWithParams } = useBatchId();
	const { showToastMessage } = useToastMessage();
	const { srcmId } = useAuth();
	const { selectedRole } = useUserSession();
	const { callApi } = useCallApi();
	const { t } = useTranslation();
	const { infoPopUpAction } = useInfoPopup();
	const { confirmPopUpAction } = useConfirmPopup();
	const navigate = useNavigate();
	const [form] = Form.useForm<BatchFormType>();
	const values = Form.useWatch<BatchFormType>([], form);
	const [isEditFormStatus, setIsEditFormStatus] = useState<boolean>(
		globalIsEditFormStatus
	);

	const navigateWithoutSrcmId = useCallback(
		(route: string, params?: NavigateOptions) => {
			navigate(`${getRouteWithParams(route, true)}`, params);
		},
		[getRouteWithParams, navigate]
	);

	const addOrEditFormStatus = useCallback(() => {
		globalIsEditFormStatus = true;
		listeners.forEach((listener) => listener());
	}, []);

	const addFunctionaryStatus = useCallback(() => {
		globalIsEditFormStatus = false;
		listeners.forEach((listener) => listener());
	}, []);

	if (!listeners.includes(() => setIsEditFormStatus(globalIsEditFormStatus))) {
		listeners.push(() => setIsEditFormStatus(globalIsEditFormStatus));
	}

	const buildCreateEditBatchRequest = useCallback(
		(batchInfo: Batch) => {
			const request: Batch = {
				name: batchInfo.name,
				description: batchInfo.description,
				startDate: batchInfo.startDate,
				endDate: batchInfo.endDate,
			};
			const batchIdFromStorage = sessionStorage.getItem('batchIdEdit');

			if (isEditFormStatus) {
				return {
					...request,
					id: batchInfo.id || parseInt(batchIdFromStorage || '0', 10),
					snpResultDateForApprover: batchInfo.snpResultDateForApprover,
					snpResultDateForCandidate: batchInfo.snpResultDateForCandidate,
					snpResultDateForProposer: batchInfo.snpResultDateForProposer,
					snpResultDateForZC: batchInfo.snpResultDateForZC,
					status: batchInfo.status,
				};
			}

			return request;
		},
		[isEditFormStatus]
	);

	const onConfirmSubmit = useCallback(async () => {
		if (batch) {
			const batchRequest = buildCreateEditBatchRequest(batch);

			try {
				await callApi(
					() =>
						isEditFormStatus
							? updateBatch(batchRequest)
							: createBatch(batchRequest),
					'errorOccurredWhileSubmittingApplication'
				);
				const successContent = isEditFormStatus
					? t('successfullyUpdated')
					: t('successfullyAdded');
				infoPopUpAction(
					successContent,
					'',
					() => {
						navigateWithoutSrcmId(BATCH_HOME);
					},
					ConfirmTick
				);
			} catch (e) {
				// handled in callApi
			}
		}
	}, [
		batch,
		buildCreateEditBatchRequest,
		callApi,
		infoPopUpAction,
		navigateWithoutSrcmId,
		t,
		isEditFormStatus,
	]);

	const onPublishDraftBatch = useCallback(async () => {
		const batchIdFromStorage = sessionStorage.getItem('batchIdEdit');
		if (srcmId && selectedRole && batchIdFromStorage && batch) {
			const batchId = parseInt(batchIdFromStorage, 10);

			try {
				const batchRequest = buildCreateEditBatchRequest(batch);
				await callApi(
					() => updateBatch(batchRequest),
					'errorOccurredWhileSubmittingApplication'
				);
				await callApi(
					() =>
						publishBatch({
							functionarySrcmId: srcmId,
							id: batchId,
							selectedRoleCode: selectedRole,
						}),
					'errorOccurredWhileSubmittingApplication'
				);
				infoPopUpAction(
					'publishedSuccessfully',
					'',
					() => {
						navigateWithoutSrcmId(BATCH_HOME);
					},
					ConfirmTick
				);
			} catch (e) {
				// handled in callApi
			}
		}
	}, [
		batch,
		buildCreateEditBatchRequest,
		callApi,
		infoPopUpAction,
		navigateWithoutSrcmId,
		selectedRole,
		srcmId,
	]);

	const onSubmitClick = useCallback(() => {
		confirmPopUpAction(
			'confirmSubmit',
			'areYouSureYouWantToSubmit',
			onConfirmSubmit
		);

		if (isEditFormStatus) {
			addOrEditFormStatus();
		}
	}, [
		onConfirmSubmit,
		confirmPopUpAction,
		isEditFormStatus,
		addOrEditFormStatus,
	]);

	const onPublishBatchClick = useCallback(() => {
		confirmPopUpAction(
			'publishBatchWarningMessage',
			'publishBatchWarningDescription',
			onPublishDraftBatch
		);
	}, [confirmPopUpAction, onPublishDraftBatch]);

	const onCancelClick = useCallback(() => {
		navigateWithoutSrcmId(BATCH_HOME);
		sessionStorage.removeItem('batchIdEdit');
		addFunctionaryStatus();
	}, [navigateWithoutSrcmId, addFunctionaryStatus]);

	const handleFormValuesChange = useCallback(async () => {
		try {
			const fieldValues: BatchFormType = await form.validateFields({
				validateOnly: true,
			});
			const startDate = formatDateToISOString(
				fieldValues.startDate?.toDate(),
				'start'
			);
			const endDate = formatDateToISOString(
				fieldValues.endDate?.toDate(),
				'end'
			);

			const transformed: Batch = {
				name: fieldValues.name ?? '',
				startDate,
				endDate,
				description: fieldValues.description ?? '',
				status: fieldValues.status,
			};

			setBatch(transformed);
			setSubmitButtonDisabled(false);
		} catch (e) {
			const fields = e as { values: Batch; outOfDate: boolean };

			if (!fields.outOfDate) {
				setBatch(fields.values);
				setSubmitButtonDisabled(true);
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [form]);

	const loadBatch = useCallback(
		async (batchId: number) => {
			if (batchId) {
				try {
					const response = await getBatch({ value: batchId });
					const batchInfo = buildCreateEditBatchRequest(response);
					setBatch(batchInfo);
					form.setFieldsValue({
						...batchInfo,
						startDate: dayjs(batchInfo.startDate).utc() as Moment,
						endDate: dayjs(batchInfo.endDate).utc() as Moment,
					});
					setSubmitButtonDisabled(false);
				} catch (e) {
					if (e instanceof Error) {
						showToastMessage('error', e.message);
					} else {
						showToastMessage('error', 'Unknown error occurred');
					}
				}
			}
		},
		[buildCreateEditBatchRequest, form, showToastMessage]
	);

	useEffect(() => {
		void handleFormValuesChange();
	}, [handleFormValuesChange, values]);

	useEffect(() => {
		const batchIdFromStorage = sessionStorage.getItem('batchIdEdit');
		if (batchIdFromStorage) {
			const parsedBatchId = parseInt(batchIdFromStorage, 10);
			void loadBatch(parsedBatchId);
		}
	}, [loadBatch]);

	return {
		form,
		batch,
		submitButtonDisabled,
		onSubmitClick,
		onCancelClick,
		values,
		isEditFormStatus,
		addOrEditFormStatus,
		addFunctionaryStatus,
		onPublishBatchClick,
	};
};
